import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDate, formatRupees } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { BellRing, CheckCircle2, X, ExternalLink, RefreshCw, ShieldCheck, FileText, Receipt, Layers, PlayCircle } from "lucide-react";

const KIND_ICON = { insurance: ShieldCheck, policy: FileText, license: Receipt, registration: Layers, other: Layers };

const TIER_META = {
  30: { cls: "bg-yellow-50 text-yellow-800 border-yellow-200", label: "30d" },
  15: { cls: "bg-amber-50 text-amber-800 border-amber-200",    label: "15d" },
  3:  { cls: "bg-red-50 text-red-700 border-red-200",           label: "3d / overdue" },
};

export default function Reminders() {
  const { user } = useAuth();
  const canRun = user?.role === "admin" || user?.role === "accounts";
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const r = await api.get(`/reminders?status=${status}`);
    setItems(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const runSweep = async () => {
    setBusy(true);
    try {
      const r = await api.post("/reminders/generate");
      toast.success(`Sweep complete — ${r.data.created} new reminder(s) generated`);
      load();
      window.dispatchEvent(new Event("reminders-changed"));
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const act = async (id, kind) => {
    try {
      await api.post(`/reminders/${id}/${kind}`);
      load();
      window.dispatchEvent(new Event("reminders-changed"));
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const grouped = { 3: [], 15: [], 30: [] };
  items.forEach((r) => (grouped[r.tier] || (grouped[r.tier] = [])).push(r));

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow">Compliance</div>
          <h1 className="mt-2 font-editorial font-light text-[38px] leading-[1.02] sm:text-5xl md:text-6xl text-slate-900 tracking-[-0.035em]" data-testid="reminders-title">
            Reminders <em className="italic">inbox.</em>
          </h1>
          <p className="mt-2 text-sm text-slate-500 max-w-lg">
            Everything that needs your attention in the next 30 days. Sweep runs automatically at 08:00 IST daily.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
          <div className="inline-flex items-center gap-1 border border-slate-200 bg-white rounded-md p-1">
            {[["pending","Pending"],["actioned","Actioned"],["dismissed","Dismissed"]].map(([v,l]) => (
              <button key={v} onClick={() => setStatus(v)} data-testid={`filter-${v}`}
                className={`px-3 py-1.5 rounded text-[12px] font-medium transition-colors ${
                  status === v ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"
                }`}>{l}</button>
            ))}
          </div>
          <div className="flex items-center gap-2 ml-auto sm:ml-0">
            {canRun && (
              <Button onClick={runSweep} disabled={busy} variant="outline" className="rounded-md" data-testid="run-sweep">
                <PlayCircle className={`h-4 w-4 mr-1.5 ${busy ? "animate-spin" : ""}`} /> Run sweep
              </Button>
            )}
            <Button onClick={load} variant="outline" className="rounded-md h-9 w-9 p-0" title="Refresh"><RefreshCw className="h-4 w-4" /></Button>
          </div>
        </div>
      </div>

      {items.length === 0 && (
        <div className="border border-dashed border-slate-200 p-10 text-center rounded-md">
          <BellRing className="h-8 w-8 mx-auto text-slate-300" />
          <div className="text-sm text-slate-500 mt-3">
            {status === "pending"
              ? "You're all caught up. Nothing needs attention right now."
              : `No ${status} reminders.`}
          </div>
        </div>
      )}

      {[3, 15, 30].map((tier) => (grouped[tier]?.length || 0) > 0 && (
        <div key={tier}>
          <div className="flex items-center gap-2 mb-3">
            <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 border rounded-sm ${TIER_META[tier].cls}`}>{TIER_META[tier].label}</span>
            <div className="text-[11px] text-slate-500 font-mono-fin">{grouped[tier].length} record(s)</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {grouped[tier].map((r) => (
              <ReminderCard key={r.id} r={r} onDismiss={() => act(r.id, "dismiss")} onAction={() => act(r.id, "action")} readOnly={status !== "pending"} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function ReminderCard({ r, onDismiss, onAction, readOnly }) {
  const KindIcon = KIND_ICON[r.policy?.kind] || Layers;
  const isOverdue = r.days_to_due !== null && r.days_to_due < 0;
  const policyTitle = r.policy?.title || "Policy no longer available";
  const policyType = r.policy?.type_name || (r.policy ? "" : "This policy was deleted");
  return (
    <div className="border border-slate-200 bg-white rounded-md p-4" data-testid={`reminder-${r.id}`}>
      <div className="flex items-start gap-3">
        <div className={`p-1.5 rounded border ${isOverdue ? "bg-red-50 border-red-200 text-red-600" : "bg-slate-50 border-slate-200 text-slate-500"}`}>
          <KindIcon className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-slate-900 leading-snug">{r.title}</div>
          <div className="text-[12px] text-slate-500 mt-0.5">
            {policyTitle} {policyType && <span className="text-slate-400">· {policyType}</span>}
          </div>
          <div className="mt-2 grid grid-cols-2 gap-2 text-[11px]">
            <div>
              <div className="text-[9px] uppercase tracking-wider text-slate-500">Due date</div>
              <div className="font-mono-fin text-slate-800 mt-0.5">{formatDate(r.due_date)}</div>
            </div>
            <div>
              <div className="text-[9px] uppercase tracking-wider text-slate-500">In</div>
              <div className={`font-mono-fin mt-0.5 ${isOverdue ? "text-red-600" : "text-slate-800"}`}>
                {r.days_to_due < 0 ? `${Math.abs(r.days_to_due)}d overdue` : `${r.days_to_due}d`}
              </div>
            </div>
          </div>
          {r.policy?.payment_link && (
            <a href={r.policy.payment_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[12px] text-red-600 hover:text-red-700 mt-2" data-testid={`pay-${r.id}`}>
              <ExternalLink className="h-3.5 w-3.5" /> Pay now
            </a>
          )}
        </div>
      </div>
      {!readOnly && (
        <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-1">
          {r.policy && (
            <Link to="/compliance" className="text-[11px] px-2 py-1 border border-slate-200 hover:bg-slate-50 rounded-sm inline-flex items-center gap-1">
              <ExternalLink className="h-3 w-3" /> Open policy
            </Link>
          )}
          <button onClick={onAction} className="ml-auto text-[11px] px-2 py-1 border border-emerald-200 text-emerald-700 hover:bg-emerald-50 rounded-sm inline-flex items-center gap-1" data-testid={`action-${r.id}`}>
            <CheckCircle2 className="h-3 w-3" /> Mark handled
          </button>
          <button onClick={onDismiss} className="text-[11px] px-2 py-1 border border-slate-200 text-slate-500 hover:bg-slate-50 rounded-sm inline-flex items-center gap-1" data-testid={`dismiss-${r.id}`}>
            <X className="h-3 w-3" /> Dismiss
          </button>
        </div>
      )}
    </div>
  );
}
